import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { api } from "./api";
import { applyTheme, DEFAULT_THEME, DEFAULT_THEME_US, THEMES } from "./themes";
import { useCountry } from "./country";

const ThemeContext = createContext();

/**
 * ThemeProvider applies a per-country theme.
 * - settings.theme_sv  → applied when country === "SV"
 * - settings.theme_us  → applied when country === "US"
 * Fallback: legacy settings.theme, then DEFAULT_THEME / DEFAULT_THEME_US.
 */
export const ThemeProvider = ({ children }) => {
    const { country } = useCountry();
    const [themeSv, setThemeSvState] = useState(DEFAULT_THEME);
    const [themeUs, setThemeUsState] = useState(DEFAULT_THEME_US);
    const [loaded, setLoaded] = useState(false);

    // Resolve the active theme based on the current country.
    const activeTheme = country === "US" ? themeUs : themeSv;

    // Apply whenever activeTheme changes.
    useEffect(() => {
        if (THEMES[activeTheme]) applyTheme(activeTheme);
    }, [activeTheme]);

    // Load persisted values once.
    useEffect(() => {
        // Quick cache from localStorage for instant paint
        try {
            const cSv = localStorage.getItem("tb_theme_sv");
            const cUs = localStorage.getItem("tb_theme_us");
            const cLegacy = localStorage.getItem("tb_theme");
            if (cSv && THEMES[cSv]) setThemeSvState(cSv);
            else if (cLegacy && THEMES[cLegacy]) setThemeSvState(cLegacy);
            if (cUs && THEMES[cUs]) setThemeUsState(cUs);
        } catch { /* ignore */ }

        api.get("/settings").then(({ data }) => {
            const sv = (data.theme_sv && THEMES[data.theme_sv]) ? data.theme_sv
                     : (data.theme && THEMES[data.theme]) ? data.theme
                     : DEFAULT_THEME;
            const us = (data.theme_us && THEMES[data.theme_us]) ? data.theme_us : DEFAULT_THEME_US;
            setThemeSvState(sv);
            setThemeUsState(us);
            try {
                localStorage.setItem("tb_theme_sv", sv);
                localStorage.setItem("tb_theme_us", us);
            } catch { /* ignore */ }
        }).catch(() => {}).finally(() => setLoaded(true));
    }, []);

    const setThemeSv = useCallback(async (key) => {
        if (!THEMES[key]) return;
        setThemeSvState(key);
        try { localStorage.setItem("tb_theme_sv", key); } catch { /* ignore */ }
        try { await api.put("/settings", { theme_sv: key }); } catch { /* ignore */ }
    }, []);

    const setThemeUs = useCallback(async (key) => {
        if (!THEMES[key]) return;
        setThemeUsState(key);
        try { localStorage.setItem("tb_theme_us", key); } catch { /* ignore */ }
        try { await api.put("/settings", { theme_us: key }); } catch { /* ignore */ }
    }, []);

    const value = useMemo(() => ({
        // active country's theme + setter
        theme: activeTheme,
        setTheme: country === "US" ? setThemeUs : setThemeSv,
        // explicit per-country accessors
        themeSv, setThemeSv,
        themeUs, setThemeUs,
        loaded,
    }), [activeTheme, country, themeSv, themeUs, loaded, setThemeSv, setThemeUs]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
